"use client";

import { Draggable, Droppable } from "@hello-pangea/dnd";
import { use, useState } from "react";
import {
  LuCircleAlert as LuAlertCircle,
  LuEllipsisVertical as LuMoreVertical,
  LuLoader as LuLoader2,
  LuMoveDown,
  LuMoveUp,
  LuMusic,
  LuPencil,
  LuSquarePlay as LuPlaySquare,
  LuTrash2,
  LuX,
} from "react-icons/lu";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import { EditorMediaType } from "@/components/app/editor-v3/constants";
import { InfoTooltip } from "@/components/shared/info-tooltip";
import { useConfirm } from "@/components/ui/confirm";
import {
  DropdownDrawer,
  DropdownDrawerContent,
  DropdownDrawerItem,
  DropdownDrawerTrigger,
} from "@/components/ui/dropdown-drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import type { MediaItem } from "@/utils/media-utils";
import type { AudioMetadata } from "@/utils/media-utils";
import {
  cancelMediaUpload,
  extractMetadataFromImage,
  formatVideoDuration,
  handleImageUpload,
  isMediaUploading,
} from "@/utils/media-utils";

import { useEditorFieldContext } from "./editor-context";

export const EditorMediaContainer = () => {
  const {
    name,
    value,
    editorContext: { mediaUploading, gated, setEditorContext },
  } = useEditorFieldContext<"media">();
  const { currentUserPublicKey } = use(UserContext);
  const { confirm } = useConfirm();

  const getMediaItems = (items: unknown) =>
    Array.isArray(items) ? (items as MediaItem[]) : [];

  const buildEditedImageItem = async (file: File) => {
    const metadata = await extractMetadataFromImage(file);

    return {
      file,
      id: crypto.randomUUID(),
      limitErrors: [],
      metadata,
      src: metadata.thumbnailDataURL,
      type: EditorMediaType.Image,
    } as MediaItem;
  };

  const replaceEditedImage = async (
    mediaItem: MediaItem,
    {
      file,
    }: {
      file: File;
      previewDataUrl: string;
    },
  ) => {
    try {
      const nextItem = await buildEditedImageItem(file);

      setEditorContext((prev) => ({
        ...prev,
        [name]: value.map((item) =>
          item.id === mediaItem.id ? nextItem : item,
        ),
      }));

      handleImageUpload(
        [nextItem],
        (updater) =>
          setEditorContext((prev) => ({
            ...prev,
            [name]:
              typeof updater === "function"
                ? updater(prev[name] as MediaItem[])
                : updater,
          })),
        currentUserPublicKey,
      );
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to update image`,
        details: createToastErrorDetails(error),
      });
      throw error;
    }
  };

  const saveImageAsCopy = async (
    mediaItem: MediaItem,
    {
      file,
    }: {
      file: File;
      previewDataUrl: string;
    },
  ) => {
    try {
      const nextItem = await buildEditedImageItem(file);
      const currentIndex = value.findIndex((item) => item.id === mediaItem.id);

      setEditorContext((prev) => {
        const nextItems = [...getMediaItems(prev[name])];
        const insertIndex =
          currentIndex >= 0 ? currentIndex + 1 : nextItems.length;

        nextItems.splice(insertIndex, 0, nextItem);

        return {
          ...prev,
          [name]: nextItems,
        };
      });

      handleImageUpload(
        [nextItem],
        (updater) =>
          setEditorContext((prev) => ({
            ...prev,
            [name]:
              typeof updater === "function"
                ? updater(prev[name] as MediaItem[])
                : updater,
          })),
        currentUserPublicKey,
      );
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to save image copy`,
        details: createToastErrorDetails(error),
      });
      throw error;
    }
  };

  const openImageEditor = (mediaItem: MediaItem) => {
    setDialog(DIALOG.ImageEditor, {
      open: true,
      historyMode: "entry",
      replace: true,
      props: {
        description:
          "Adjust the image and replace the current upload, or save a new copy beside it.",
        onSave: (payload) => replaceEditedImage(mediaItem, payload),
        onSaveAsCopy: (payload) => saveImageAsCopy(mediaItem, payload),
        source: {
          fileName: mediaItem.file.name,
          src:
            typeof mediaItem.metadata?.url === "string"
              ? mediaItem.metadata.url
              : mediaItem.src,
        },
        title: "Edit Image",
        variant: "post-image",
      },
    });
  };

  const removeMediaItem = (mediaItem: MediaItem) => {
    cancelMediaUpload(mediaItem.id);

    setEditorContext((prev) => ({
      ...prev,
      [name]: getMediaItems(prev[name]).filter(
        (item) => item.id !== mediaItem.id,
      ),
    }));
  };

  return (
    <>
      <div className="@container w-full">
        <Droppable
          droppableId={name}
          direction="horizontal"
          isDropDisabled={mediaUploading}
        >
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="
                grid w-full grid-cols-1
                @min-[200px]:grid-cols-4
                @min-[400px]:grid-cols-6
                @min-[600px]:grid-cols-8
              "
            >
              {value.map((mediaItem, index) => {
                const isUploading = isMediaUploading(mediaItem);

                return (
                  <Draggable // We add index so that we force a rerender of the video player,
                    // otherwise reordering can mess it up
                    key={`${mediaItem.id}_${index}`}
                    draggableId={mediaItem.id}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="mt-4 pr-2"
                      >
                        <div
                          className="
                            group relative mr-2 h-20 w-full rounded-xl border
                            bg-background p-1
                            hover:border-primary
                          "
                        >
                          <MediaItem media={mediaItem} index={index} />
                          <EditorMediaItemActions
                            onCancelUpload={
                              isUploading
                                ? () => removeMediaItem(mediaItem)
                                : undefined
                            }
                            onEditAudioTitle={
                              !isUploading &&
                              mediaItem.type === EditorMediaType.Audio
                                ? async () => {
                                    const newMedia = structuredClone(mediaItem);
                                    const metadata =
                                      newMedia.metadata as AudioMetadata;
                                    const confirmed = await confirm({
                                      title: "Edit Audio Title",
                                      description: (
                                        <div>
                                          <Label>Title</Label>
                                          <Input
                                            className="w-full"
                                            defaultValue={metadata.title}
                                            maxLength={60}
                                            onChange={(ev) => {
                                              if (
                                                ev.target.value.length >
                                                ev.target.maxLength
                                              ) {
                                                ev.preventDefault();
                                                return;
                                              }
                                              (
                                                newMedia.metadata as AudioMetadata
                                              ).title = ev.target.value;
                                            }}
                                          />
                                        </div>
                                      ),
                                    });

                                    if (confirmed) {
                                      setEditorContext((prev) => ({
                                        ...prev,
                                        [name]: value.map((item) =>
                                          item.id === mediaItem.id
                                            ? newMedia
                                            : item,
                                        ),
                                      }));
                                    }
                                  }
                                : undefined
                            }
                            onEditImage={
                              !isUploading &&
                              mediaItem.type === EditorMediaType.Image
                                ? () => openImageEditor(mediaItem)
                                : undefined
                            }
                            onMoveToGated={
                              !isUploading && gated() && name === "media"
                                ? () => {
                                    setEditorContext((prev) => ({
                                      ...prev,
                                      media: value.filter(
                                        (item) => item.id !== mediaItem.id,
                                      ),
                                      gatedMedia: [
                                        ...prev.gatedMedia!,
                                        mediaItem,
                                      ],
                                    }));
                                  }
                                : undefined
                            }
                            onMoveToPreview={
                              !isUploading && gated() && name === "gatedMedia"
                                ? () => {
                                    setEditorContext((prev) => ({
                                      ...prev,
                                      gatedMedia: value.filter(
                                        (item) => item.id !== mediaItem.id,
                                      ),
                                      media: [...prev.media!, mediaItem],
                                    }));
                                  }
                                : undefined
                            }
                            onRemove={() => removeMediaItem(mediaItem)}
                          />
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </>
  );
};

type EditorMediaItemActionsProps = {
  onCancelUpload?: () => void;
  onEditAudioTitle?: () => void | Promise<void>;
  onEditImage?: () => void;
  onMoveToGated?: () => void;
  onMoveToPreview?: () => void;
  onRemove: () => void;
};

const mediaActionItemClassName = "gap-2.5";
const mediaActionIconClassName = "size-4 shrink-0";
const mediaActionLabelClassName = "min-w-0 flex-1 truncate";

const EditorMediaItemActions = ({
  onCancelUpload,
  onEditAudioTitle,
  onEditImage,
  onMoveToGated,
  onMoveToPreview,
  onRemove,
}: EditorMediaItemActionsProps) => {
  const [open, setOpen] = useState(false);

  return (
    <DropdownDrawer
      historyName="editor-media-item-actions"
      onOpenChange={setOpen}
      open={open}
      title="Media actions"
    >
      <DropdownDrawerTrigger
        render={
          <button
            aria-label="Open media actions"
            type="button"
            className="
              absolute top-2 right-2 z-30 rounded-full bg-background
              p-1
              text-muted-foreground
              group-hover:visible
              hover:bg-muted
              data-popup-open:visible
              data-[state=open]:visible
              md:invisible
            "
          >
            <LuMoreVertical />
          </button>
        }
      />
      <DropdownDrawerContent align="end" side="bottom">
        {onCancelUpload ? (
          <DropdownDrawerItem
            className={mediaActionItemClassName}
            onClick={onCancelUpload}
          >
            <LuX className={mediaActionIconClassName} />
            <span className={mediaActionLabelClassName}>Cancel upload</span>
          </DropdownDrawerItem>
        ) : (
          <>
            {onMoveToGated && (
              <DropdownDrawerItem
                className={mediaActionItemClassName}
                onClick={onMoveToGated}
              >
                <LuMoveDown className={mediaActionIconClassName} />
                <span className={mediaActionLabelClassName}>Move to Gated</span>
              </DropdownDrawerItem>
            )}
            {onMoveToPreview && (
              <DropdownDrawerItem
                className={mediaActionItemClassName}
                onClick={onMoveToPreview}
              >
                <LuMoveUp className={mediaActionIconClassName} />
                <span className={mediaActionLabelClassName}>
                  Move to Preview
                </span>
              </DropdownDrawerItem>
            )}
            {onEditImage && (
              <DropdownDrawerItem
                className={mediaActionItemClassName}
                closeOnClick={false}
                onClick={() => {
                  onEditImage();
                  setOpen(false);
                }}
              >
                <LuPencil className={mediaActionIconClassName} />
                <span className={mediaActionLabelClassName}>Edit image</span>
              </DropdownDrawerItem>
            )}
            {onEditAudioTitle && (
              <DropdownDrawerItem
                className={mediaActionItemClassName}
                onClick={onEditAudioTitle}
              >
                <LuPencil className={mediaActionIconClassName} />
                <span className={mediaActionLabelClassName}>Edit title</span>
              </DropdownDrawerItem>
            )}
            <DropdownDrawerItem
              className={mediaActionItemClassName}
              onClick={onRemove}
            >
              <LuTrash2 className={mediaActionIconClassName} />
              <span className={mediaActionLabelClassName}>Remove</span>
            </DropdownDrawerItem>
          </>
        )}
      </DropdownDrawerContent>
    </DropdownDrawer>
  );
};

interface MediaItemProps {
  media: MediaItem;
  index: number;
}

const MediaItem = ({ media, index }: MediaItemProps) => {
  const isUploading = isMediaUploading(media);
  const failureMessage = media.uploadFailed
    ? `Upload failed for ${media.file.name}. Remove it and try again.`
    : "";

  return (
    <>
      {isUploading && (
        <>
          <Progress
            className="absolute bottom-3 left-[10px] z-100 w-[calc(100%-20px)]"
            value={media.progress || 0}
            getValueLabel={(value) => `${Math.round(value)}%`}
          />
          <div
            className="
              pointer-events-none absolute inset-0 z-10 flex items-center
              justify-center rounded-xl bg-black/30
            "
          >
            <div className="absolute inset-0 z-20 flex items-center justify-center">
              <LuLoader2 className="relative -top-1 animate-spin stroke-white text-2xl" />
            </div>
          </div>
        </>
      )}

      {media.uploadFailed && (
        <InfoTooltip
          className="max-w-60"
          mobile={false}
          side="top"
          text={
            <span className="block text-xs">
              Upload failed for{" "}
              <span className="font-medium break-all">{media.file.name}</span>.
              Remove it and try again.
            </span>
          }
        >
          <span
            aria-label={failureMessage}
            className="
              absolute top-2 left-2 z-20 flex size-7 items-center
              justify-center rounded-full border border-destructive/50
              bg-background/95 text-destructive shadow-sm
            "
            role="img"
            title={failureMessage}
          >
            <LuAlertCircle className="size-4" />
          </span>
        </InfoTooltip>
      )}

      {media.type !== EditorMediaType.Audio && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={media.src}
          alt={`Thumbnail ${index}`}
          className="
            size-full cursor-grabbing rounded-sm object-cover
            data-[upload-failed=true]:opacity-50
          "
          data-upload-failed={media.uploadFailed ? true : undefined}
        />
      )}

      {media.type === EditorMediaType.Audio && (
        <div className="p-1.5">
          <div
            className="
              line-clamp-3 overflow-hidden text-center text-xs wrap-break-word
              data-[upload-failed=true]:opacity-50
            "
            data-upload-failed={media.uploadFailed ? true : undefined}
          >
            {(media.metadata as AudioMetadata).title}
          </div>
        </div>
      )}

      {!isUploading &&
        !media.uploadFailed &&
        media.type === EditorMediaType.Video && (
          <div
            className="
            absolute inset-x-0 bottom-1 m-auto flex w-full items-center
            justify-center gap-1 bg-background/80 pt-1 text-center text-xs
            leading-none font-bold text-muted-foreground
          "
          >
            <LuPlaySquare />
            {formatVideoDuration(media.duration || 0)}
          </div>
        )}

      {!isUploading &&
        !media.uploadFailed &&
        media.type === EditorMediaType.Audio && (
          <div
            className="
            absolute inset-x-0 bottom-1 m-auto flex w-full items-center
            justify-center gap-1 bg-background/80 pt-1 text-center text-xs
            leading-none font-bold text-muted-foreground
          "
          >
            <LuMusic />
            {formatVideoDuration(media.duration || 0)}
          </div>
        )}
    </>
  );
};
